/**
 * PURPOSE: Token Bearer del staff en el navegador (FTR-0019).
 * RESPONSIBILITIES: Guardar/leer/borrar el JWT y su expiración en localStorage (paridad con la cookie
 *   persistente de 24 h; compartido entre pestañas; se descarta localmente al vencer); adjuntar
 *   `Authorization: Bearer` sólo a requests dirigidos a la API de Winscore (nunca a hosts externos,
 *   nunca a /login, nunca pisando una cabecera explícita); tratar un 401 de la API como sesión
 *   inválida (borra el token y avisa); notificar el logout hecho en otra pestaña (evento storage).
 *   Sin dependencias de axios para poder probarse de forma aislada.
 * COLLABORATORS: src/services/httpService.js, src/index.js (instancia axios por defecto),
 *   src/features/authSlice.js, src/components/global/AuthSessionBridge.jsx,
 *   volleyballViewerControlService.js (handshake Socket.IO), ModalMatchObservationBasquetball.jsx (fetch).
 * ROUTE: winscore/src/services/authToken.js
 */
import { targetsApiBase } from "./effectiveCompanyContext.js";

export const AUTH_TOKEN_STORAGE_KEY = "winscore.auth.token";
export const AUTH_TOKEN_EXPIRES_STORAGE_KEY = "winscore.auth.expiresAt";
export const AUTH_UNAUTHORIZED_EVENT = "winscore:unauthorized";
const AUTHORIZATION_HEADER = "Authorization";

const storage = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
};
const read = (key) => {
  try {
    return storage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
};
const write = (key, value) => {
  try {
    storage()?.setItem(key, value);
  } catch {
    /* almacenamiento no disponible: la sesión dura lo que dure la pestaña */
  }
};
const remove = (key) => {
  try {
    storage()?.removeItem(key);
  } catch {
    /* nada que limpiar */
  }
};

export const clearAuthToken = () => {
  remove(AUTH_TOKEN_STORAGE_KEY);
  remove(AUTH_TOKEN_EXPIRES_STORAGE_KEY);
};

export const getAuthTokenExpiresAt = () => {
  const raw = read(AUTH_TOKEN_EXPIRES_STORAGE_KEY);
  const at = raw ? Date.parse(raw) : NaN;
  return Number.isFinite(at) ? at : null;
};

/** Token vigente o null; un token vencido según su expiración local se descarta al leerlo. */
export const getAuthToken = () => {
  const token = read(AUTH_TOKEN_STORAGE_KEY);
  if (!token) return null;
  const expiresAt = getAuthTokenExpiresAt();
  if (expiresAt !== null && expiresAt <= Date.now()) {
    clearAuthToken();
    return null;
  }
  return token;
};

export const setAuthToken = (token, expiresAt) => {
  if (typeof token !== "string" || token === "") {
    clearAuthToken();
    return;
  }
  write(AUTH_TOKEN_STORAGE_KEY, token);
  const at = expiresAt ? Date.parse(expiresAt) : NaN;
  if (Number.isFinite(at)) write(AUTH_TOKEN_EXPIRES_STORAGE_KEY, new Date(at).toISOString());
  else remove(AUTH_TOKEN_EXPIRES_STORAGE_KEY);
};

export const hasAuthToken = () => getAuthToken() !== null;

/** Cabeceras para fetch: añade Authorization cuando hay token. */
export const authHeaders = (headers = {}) => {
  const token = getAuthToken();
  return token ? { ...headers, [AUTHORIZATION_HEADER]: `Bearer ${token}` } : { ...headers };
};

const pathOf = (url) => String(url || "").split("?")[0].split("#")[0].replace(/\/+$/, "");
/** POST /login no lleva token: un token vencido en storage no debe impedir volver a iniciar sesión. */
export const isLoginRequest = (config) => /(^|\/)login$/.test(pathOf(config?.url));

const readHeader = (headers, name) => {
  if (!headers) return undefined;
  if (typeof headers.get === "function") {
    const value = headers.get(name);
    if (value !== undefined && value !== null) return value;
  }
  const key = Object.keys(headers).find((header) => header.toLowerCase() === name.toLowerCase());
  return key ? headers[key] : undefined;
};

/**
 * Añade Authorization: Bearer cuando hay token y el destino es la API de Winscore (nunca /login).
 * Una cabecera explícita ya presente se respeta. Compatible con AxiosHeaders (set/get) y objetos planos.
 */
export const attachAuthHeader = (config, { apiBaseUrl, readToken = getAuthToken } = {}) => {
  if (!config || !targetsApiBase(config, apiBaseUrl) || isLoginRequest(config)) return config;

  const headers = config.headers || {};
  const existing = readHeader(headers, AUTHORIZATION_HEADER);
  if (existing !== undefined && existing !== null && existing !== "") {
    config.headers = headers;
    return config;
  }

  const token = readToken();
  if (!token) return config;

  if (typeof headers.set === "function") headers.set(AUTHORIZATION_HEADER, `Bearer ${token}`);
  else headers[AUTHORIZATION_HEADER] = `Bearer ${token}`;
  config.headers = headers;
  return config;
};

/** Interceptor de request para cualquier instancia axios; lee el token en cada request. */
export const createAuthHeaderInterceptor = (apiBaseUrl, readToken = getAuthToken) => (config) =>
  attachAuthHeader(config, { apiBaseUrl, readToken });

/** 401 de la API de Winscore (salvo /login): el token ya no sirve (expirado, revocado o inválido). */
export const isUnauthorizedApiError = (error, apiBaseUrl) =>
  Number(error?.response?.status) === 401 &&
  Boolean(error?.config) &&
  targetsApiBase(error.config, apiBaseUrl) &&
  !isLoginRequest(error.config);

/**
 * Interceptor de respuesta (rama de error): ante un 401 de la API borra el token y avisa; la
 * promesa sigue rechazada para que cada llamada conserve su manejo de error.
 */
export const createUnauthorizedResponseInterceptor = (apiBaseUrl, onUnauthorized) => (error) => {
  if (isUnauthorizedApiError(error, apiBaseUrl)) {
    clearAuthToken();
    if (typeof onUnauthorized === "function") {
      try {
        onUnauthorized(error);
      } catch {
        /* el aviso nunca debe enmascarar el error original */
      }
    }
  }
  return Promise.reject(error);
};

const canListen = () => typeof window !== "undefined" && typeof window.addEventListener === "function";

export const dispatchUnauthorized = (detail) => {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
  window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT, { detail }));
};

export const subscribeUnauthorized = (callback) => {
  if (!canListen()) return () => {};
  const handler = (event) => callback(event);
  window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handler);
  return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handler);
};

/**
 * Logout hecho en otra pestaña: el evento storage sólo se dispara en las demás pestañas del mismo
 * origen. Se avisa cuando el token desaparece (removeItem) o cuando se vacía el storage (clear).
 */
export const subscribeAuthTokenRemoved = (callback) => {
  if (!canListen()) return () => {};
  const handler = (event) => {
    const cleared = event.key === null;
    const removed = event.key === AUTH_TOKEN_STORAGE_KEY && !event.newValue;
    if (cleared || removed) callback(event);
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
};
